import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { Clock, Calendar, User, ArrowLeft, Share2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Section } from "@/components/Primitives";
import { CTABand } from "@/components/PageHero";
import { fetchInsight } from "@/lib/api";
import NotFound from "@/pages/NotFound";
import { toast } from "sonner";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined);

  useEffect(() => {
    fetchInsight(slug).then(setArticle).catch(() => setArticle(null));
  }, [slug]);

  if (article === null) return <NotFound />;
  if (!article) return <div className="pt-40 pb-20 text-center text-slate-400">Loading…</div>;

  const share = () => {
    if (navigator.share) navigator.share({ title: article.title, url: window.location.href });
    else { navigator.clipboard.writeText(window.location.href); toast.success("Link copied"); }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article", headline: article.title,
    author: { "@type": "Organization", name: article.author },
    datePublished: article.published_date, dateModified: article.last_updated,
    articleSection: article.category,
  };

  return (
    <>
      <SEO title={article.title} description={article.excerpt} path={`/insights/${slug}`} jsonLd={jsonLd} />
      <article>
        <div className="relative bg-navy text-white pt-32 pb-16 md:pt-40">
          <div className="absolute inset-0"><img src={article.image} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/60" /></div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
            <Link to="/insights" className="inline-flex items-center gap-2 text-gold text-sm font-semibold mb-4"><ArrowLeft size={16} /> Back to Insights</Link>
            <div><span className="text-xs font-bold uppercase tracking-wide text-gold">{article.category}</span></div>
            <h1 className="mt-3 text-3xl md:text-4xl font-extrabold leading-tight">{article.title}</h1>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><User size={14} /> {article.author}</span>
              <span className="flex items-center gap-1.5"><Calendar size={14} /> Published {article.published_date}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} /> {article.reading_time}</span>
            </div>
          </div>
        </div>

        <Section className="bg-white">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-wrap gap-4 text-xs text-slate-400 border-b border-slate-100 pb-4 mb-6">
              <span>Last reviewed: {article.last_reviewed}</span>
              <span>Last updated: {article.last_updated}</span>
              <button onClick={share} className="ml-auto inline-flex items-center gap-1.5 text-emerald-brand font-semibold"><Share2 size={14} /> Share</button>
            </div>
            <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed [&_p]:mb-4 [&_p]:text-[17px]" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body) }} />

            <div className="mt-10 rounded-xl bg-slate-50 border border-slate-200 p-5 text-sm text-slate-500">
              <strong className="text-navy">Disclaimer:</strong> This article is general information only and is not legal, tax or immigration advice. Company formation, licensing, tax treatment, banking, work permits and immigration outcomes depend on your circumstances, applicable laws and decisions made by third parties or government authorities. No outcome is guaranteed. Contact us to know more.
            </div>

            {article.related?.length > 0 && (
              <div className="mt-12">
                <h3 className="font-bold text-navy mb-4">Related Articles</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {article.related.map((r) => (
                    <Link key={r.slug} to={`/insights/${r.slug}`} className="rounded-xl border border-slate-100 overflow-hidden hover-lift">
                      <img src={r.image} alt="" className="h-24 w-full object-cover" />
                      <div className="p-4"><div className="text-xs text-emerald-brand font-semibold">{r.category}</div><div className="text-sm font-bold text-navy mt-1 line-clamp-2">{r.title}</div></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Section>
      </article>
      <CTABand />
    </>
  );
}
